import { PayloadAction } from '@reduxjs/toolkit';
import { notification } from 'antd';
import { globalActions } from 'app/containers/Global/slice';
import { RouteConstants } from 'app/routes';
import i18next from 'i18next';
import { generatePath, matchPath } from 'react-router';
import { put, takeLatest } from 'redux-saga/effects';
import { localStorageEnum } from 'types';
import { hiikoEnvActions as actions } from '.';
import hiikoEnvApi from '../hiikoEnvApi';
import { ICreateEnvPayload, IEnv } from './types';

function* initLoadEnvsSaga() {
  try {
    const envs: IEnv[] = yield hiikoEnvApi.getEnvs();
    yield put(actions.setEnvs(envs));
    const isAuthRoute = matchPath(
      window.location.pathname,
      RouteConstants.auth.index,
    );

    if (isAuthRoute) {
      const envIdInLocalStorage = localStorage.getItem(localStorageEnum.envId);
      const isValidEnvId = envs.find(
        envInList => envInList.id === Number(envIdInLocalStorage),
      );
      if (isValidEnvId) {
        yield put(
          globalActions.setRedirectTo(
            generatePath(RouteConstants.company.list, {
              envId: Number(envIdInLocalStorage),
            }),
          ),
        );
      } else {
        yield put(globalActions.setRedirectTo(RouteConstants.hiikoEnv.list));
      }
    }
  } catch (err) {
    yield put(globalActions.handleError(err));
  } finally {
    yield put(actions.setInitEnvsLoading(false));
  }
}

function* createEnvSaga(action: PayloadAction<ICreateEnvPayload>) {
  try {
    const { countryCode, envName } = action.payload;
    yield hiikoEnvApi.createEnv(countryCode, envName);

    yield put(globalActions.setRedirectTo(RouteConstants.hiikoEnv.list));

    const envs: IEnv[] = yield hiikoEnvApi.getEnvs();

    yield put(actions.setEnvs(envs));

    notification.success({
      message: i18next?.t('HiikoEnv.Notification.Created', {
        defaultValue:
          'The environment “{{envName}}” has been successfully created',
        envName,
      }),
      placement: 'bottomRight',
    });
  } catch (err) {
    yield put(globalActions.handleError(err));
  } finally {
    yield put(actions.setCreateEnvLoading(false));
  }
}

function* archiveEnvSaga(action: PayloadAction<number>) {
  try {
    const envId = action.payload;
    yield hiikoEnvApi.archiveHiikoEnvironment(envId);

    const envs: IEnv[] = yield hiikoEnvApi.getEnvs();

    yield put(actions.setEnvs(envs));

    notification.success({
      message: i18next?.t('HiikoEnv.Notification.EnvIsArchived', {
        defaultValue: 'The environment has been successfully archived.',
      }),
      placement: 'bottomRight',
    });
  } catch (err) {
    yield put(globalActions.handleError(err));
  } finally {
  }
}

export function* hiikoEnvSaga() {
  yield takeLatest(actions.initLoadEnvs.type, initLoadEnvsSaga);
  yield takeLatest(actions.createEnv.type, createEnvSaga);
  yield takeLatest(actions.archiveEnv.type, archiveEnvSaga);
}
